import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { tableStyle, thStyle, trStyle, smallBtn } from '../lib/styles';

const FUEL_MPG = 7;

function calcDriverPay(rate: number, miles: number, driver: any): number {
  if (!driver) return 0;
  if (driver.pay_type === 'PER_MILE') return (driver.pay_rate_per_mile || 0) * miles;
  if (driver.pay_type === 'FLAT') return driver.pay_rate_per_mile || 0;
  return rate * ((driver.pay_rate_percent || driver.pay_percent || 25) / 100);
}

function healthColor(score: number) { return score >= 75 ? '#32CD32' : score >= 50 ? '#FFD700' : '#ff4444'; }

function exportCSV(rows: any[], filename: string) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]).join(',');
  const data = rows.map(r => Object.values(r).map(v => JSON.stringify(v ?? '')).join(',')).join('\n');
  const blob = new Blob([headers + '\n' + data], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
}

interface FinancialsProps { companyId: string | null; }

export default function Financials({ companyId }: FinancialsProps) {
  const [period, setPeriod] = useState<'7'|'30'|'90'|'month'|'custom'>('30');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [loading, setLoading] = useState(false);
  const [truckSort, setTruckSort] = useState<{col:string;dir:'asc'|'desc'}>({col:'net',dir:'desc'});
  const [driverSort, setDriverSort] = useState<{col:string;dir:'asc'|'desc'}>({col:'netContribution',dir:'desc'});
  const [laneSort, setLaneSort] = useState<{col:string;dir:'asc'|'desc'}>({col:'totalNet',dir:'desc'});
  const [brief, setBrief] = useState<any>(null);
  const [truckData, setTruckData] = useState<any[]>([]);
  const [driverData, setDriverData] = useState<any[]>([]);
  const [laneData, setLaneData] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [drillTruck, setDrillTruck] = useState<any>(null);

  const getDateRange = useCallback(() => {
    const now = new Date();
    if (period === 'custom') return { start: customStart, end: customEnd };
    if (period === 'month') {
      const first = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      return { start: first, end: now.toISOString().split('T')[0] };
    }
    const days = Number(period);
    const start = new Date(Date.now() - days * 86400000).toISOString().split('T')[0];
    return { start, end: now.toISOString().split('T')[0] };
  }, [period, customStart, customEnd]);

  const fetchData = useCallback(async () => {
    if (!companyId) return;
    const { start, end } = getDateRange();
    if (!start || !end) return;
    setLoading(true);

    const [loadsRes, driversRes, assetsRes, fuelRes, maintRes, briefRes] = await Promise.all([
      supabase.from('loads').select('*').eq('company_id', companyId).gte('delivery_date', start).lte('delivery_date', end).eq('status', 'DELIVERED'),
      supabase.from('drivers').select('*').eq('company_id', companyId),
      supabase.from('assets').select('*').eq('company_id', companyId),
      supabase.from('fuel_logs').select('*').eq('company_id', companyId).gte('logged_at', start).lte('logged_at', end),
      supabase.from('maintenance').select('*').eq('company_id', companyId).gte('last_service', start).lte('last_service', end),
      supabase.from('fleet_financial_snapshots').select('*').eq('company_id', companyId).order('created_at', { ascending: false }).limit(1).maybeSingle(),
    ]);

    const loads = loadsRes.data || [];
    const drivers = driversRes.data || [];
    const assets = assetsRes.data || [];
    const fuel = fuelRes.data || [];
    const maint = maintRes.data || [];
    setBrief(briefRes.data || null);

    const driversById: Record<string, any> = {};
    drivers.forEach((d: any) => { driversById[d.id] = d; });

    // Fleet summary
    const totalRevenue = loads.reduce((s: number, l: any) => s + Number(l.rate_total || 0), 0);
    const totalFuel = fuel.reduce((s: number, f: any) => s + Number(f.total_cost || 0), 0);
    const totalMaint = maint.reduce((s: number, m: any) => s + Number(m.cost || 0), 0);
    const totalLoadedMiles = loads.reduce((s: number, l: any) => s + Number(l.loaded_miles || 0), 0);
    const totalAllMiles = loads.reduce((s: number, l: any) => s + Number(l.loaded_miles || 0) + Number(l.deadhead_miles || 0), 0);
    let totalDriverPay = 0;
    loads.forEach((l: any) => { totalDriverPay += calcDriverPay(Number(l.rate_total || 0), Number(l.loaded_miles || 0), driversById[l.assigned_driver_id]); });
    const totalNet = totalRevenue - totalFuel - totalDriverPay - totalMaint;
    const avgRpm = totalLoadedMiles > 0 ? totalRevenue / totalLoadedMiles : 0;
    const margin = totalRevenue > 0 ? (totalNet / totalRevenue) * 100 : 0;
    setSummary({ totalRevenue, totalFuel, totalDriverPay, totalMaint, totalNet, totalLoadedMiles, totalAllMiles, avgRpm, margin, loads: loads.length });

    // Per-truck
    const truckMap: Record<string, any> = {};
    assets.forEach((a: any) => { truckMap[a.id] = { unitNumber: a.unit_number, assetId: a.id, assetType: a.asset_type, revenue: 0, fuel: 0, driverPay: 0, maint: 0, loadedMiles: 0, allMiles: 0, loads: 0, health: a.financial_health_score || 100 }; });
    loads.forEach((l: any) => {
      const t = truckMap[l.assigned_asset_id]; if (!t) return;
      t.revenue += Number(l.rate_total || 0);
      t.loadedMiles += Number(l.loaded_miles || 0);
      t.allMiles += Number(l.loaded_miles || 0) + Number(l.deadhead_miles || 0);
      t.driverPay += calcDriverPay(Number(l.rate_total || 0), Number(l.loaded_miles || 0), driversById[l.assigned_driver_id]);
      t.loads++;
    });
    fuel.forEach((f: any) => { const a = assets.find((x: any) => x.unit_number === f.truck_number); if (a && truckMap[a.id]) truckMap[a.id].fuel += Number(f.total_cost || 0); });
    maint.forEach((m: any) => { const a = assets.find((x: any) => x.unit_number === m.unit_number); if (a && truckMap[a.id]) truckMap[a.id].maint += Number(m.cost || 0); });
    const trucks = Object.values(truckMap).filter((t: any) => t.loads > 0).map((t: any) => ({
      ...t,
      net: t.revenue - t.fuel - t.driverPay - t.maint,
      rpm: t.loadedMiles > 0 ? t.revenue / t.loadedMiles : 0,
      cpm: t.allMiles > 0 ? (t.fuel + t.driverPay + t.maint) / t.allMiles : 0,
      margin: t.revenue > 0 ? ((t.revenue - t.fuel - t.driverPay - t.maint) / t.revenue) * 100 : 0,
    }));
    setTruckData(trucks);

    // Per-driver
    const driverMap: Record<string, any> = {};
    drivers.forEach((d: any) => { driverMap[d.id] = { driverId: d.id, name: d.full_name, loads: 0, revenue: 0, pay: 0, loadedMiles: 0, onTime: 0 }; });
    loads.forEach((l: any) => {
      const dm = driverMap[l.assigned_driver_id]; if (!dm) return;
      dm.loads++;
      dm.revenue += Number(l.rate_total || 0);
      dm.pay += calcDriverPay(Number(l.rate_total || 0), Number(l.loaded_miles || 0), driversById[l.assigned_driver_id]);
      dm.loadedMiles += Number(l.loaded_miles || 0);
      if (l.delivered_at && l.delivery_date && new Date(l.delivered_at) <= new Date(l.delivery_date)) dm.onTime++;
    });
    setDriverData(Object.values(driverMap).filter((d: any) => d.loads > 0).map((d: any) => ({
      ...d, netContribution: d.revenue - d.pay,
      avgRpm: d.loadedMiles > 0 ? d.revenue / d.loadedMiles : 0,
      onTimeRate: d.loads > 0 ? (d.onTime / d.loads) * 100 : 0,
    })));

    // Per-lane
    const laneMap: Record<string, any> = {};
    loads.forEach((l: any) => {
      if (!l.pickup_city || !l.delivery_city) return;
      const key = `${l.pickup_city.toUpperCase()},${(l.pickup_state||'').toUpperCase()}→${l.delivery_city.toUpperCase()},${(l.delivery_state||'').toUpperCase()}`;
      if (!laneMap[key]) laneMap[key] = { lane: key, origin: `${l.pickup_city}, ${l.pickup_state||''}`, destination: `${l.delivery_city}, ${l.delivery_state||''}`, loads: 0, totalRevenue: 0, totalRpm: 0, totalMargin: 0, totalNet: 0 };
      const lm = laneMap[key];
      lm.loads++; lm.totalRevenue += Number(l.rate_total || 0); lm.totalRpm += Number(l.rpm || 0); lm.totalMargin += Number(l.profit_margin_percent || 0); lm.totalNet += Number(l.net_profit || 0);
    });
    setLaneData(Object.values(laneMap).map((l: any) => ({ ...l, avgRate: l.loads > 0 ? l.totalRevenue / l.loads : 0, avgRpm: l.loads > 0 ? l.totalRpm / l.loads : 0, avgMargin: l.loads > 0 ? l.totalMargin / l.loads : 0 })));

    setLoading(false);
  }, [companyId, getDateRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const sortTable = (data: any[], sort: { col: string; dir: 'asc' | 'desc' }) =>
    [...data].sort((a, b) => sort.dir === 'desc' ? (b[sort.col] || 0) - (a[sort.col] || 0) : (a[sort.col] || 0) - (b[sort.col] || 0));

  const toggleSort = (col: string, current: { col: string; dir: 'asc'|'desc' }, set: (v: any) => void) =>
    set({ col, dir: current.col === col && current.dir === 'desc' ? 'asc' : 'desc' });

  const Th = ({ col, label, sort, setSort }: any) => (
    <th onClick={() => toggleSort(col, sort, setSort)}
      style={{ ...thStyle, cursor: 'pointer', userSelect: 'none' }}>
      {label}{sort.col === col ? (sort.dir === 'desc' ? ' ▼' : ' ▲') : ''}
    </th>
  );

  const fi: React.CSSProperties = { background: '#0a0a0a', border: '1px solid #2a2a2a', color: '#fff', padding: '6px 10px', fontFamily: 'monospace', fontSize: '11px' };
  const periodBtns = [['7','7D'],['30','30D'],['90','90D'],['month','MTD'],['custom','CUSTOM']] as const;

  return (
    <div>
      {/* Period Selector */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
        {periodBtns.map(([val, label]) => (
          <button key={val} onClick={() => setPeriod(val as any)}
            style={{ padding: '6px 16px', fontFamily: 'monospace', fontSize: '11px', fontWeight: 700, cursor: 'pointer', border: `1px solid ${period === val ? '#32CD32' : '#333'}`, background: period === val ? '#0a2a0a' : 'transparent', color: period === val ? '#32CD32' : '#555' }}>
            {label}
          </button>
        ))}
        {period === 'custom' && (
          <>
            <input type="date" style={{ ...fi }} value={customStart} onChange={e => setCustomStart(e.target.value)} />
            <span style={{ color: '#555' }}>to</span>
            <input type="date" style={{ ...fi }} value={customEnd} onChange={e => setCustomEnd(e.target.value)} />
            <button onClick={fetchData} style={smallBtn}>GO</button>
          </>
        )}
        {loading && <span style={{ color: '#555', fontSize: '11px' }}>Calculating...</span>}
      </div>

      {/* Fleet Summary Cards */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: '12px', marginBottom: '24px' }}>
          {[
            { label: 'TOTAL REVENUE', val: `$${summary.totalRevenue.toLocaleString()}`, color: '#32CD32' },
            { label: 'NET PROFIT', val: `$${summary.totalNet.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`, color: summary.totalNet >= 0 ? '#32CD32' : '#ff4444' },
            { label: 'MARGIN', val: `${summary.margin.toFixed(1)}%`, color: summary.margin >= 25 ? '#32CD32' : summary.margin >= 10 ? '#FFD700' : '#ff4444' },
            { label: 'AVG RPM', val: `$${summary.avgRpm.toFixed(2)}/mi`, color: summary.avgRpm >= 2 ? '#32CD32' : summary.avgRpm >= 1.5 ? '#FFD700' : '#ff4444' },
            { label: 'TOTAL MILES', val: summary.totalLoadedMiles.toLocaleString(), color: '#aaa' },
          ].map((k, i) => (
            <div key={i} style={{ background: '#050505', border: '1px solid #1a1a1a', padding: '16px' }}>
              <div style={{ color: '#555', fontSize: '9px', letterSpacing: '1.5px', marginBottom: '6px' }}>{k.label}</div>
              <div style={{ color: k.color, fontSize: '22px', fontWeight: 900 }}>{k.val}</div>
              {i === 0 && <div style={{ color: '#444', fontSize: '10px', marginTop: '4px' }}>{summary.loads} loads delivered</div>}
            </div>
          ))}
        </div>
      )}

      {/* Jake Weekly Brief */}
      {brief?.jake_brief && (
        <div style={{ border: '1px solid #1e3a1e', background: '#050f05', padding: '16px', marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <div style={{ color: '#32CD32', fontSize: '10px', letterSpacing: '2px', fontWeight: 700 }}>⚡ JAKE'S WEEKLY BRIEF — {brief.period_start} to {brief.period_end}</div>
            <button onClick={fetchData} style={{ background: 'transparent', border: '1px solid #32CD32', color: '#32CD32', padding: '4px 12px', fontFamily: 'monospace', fontSize: '10px', cursor: 'pointer' }}>↻ REFRESH</button>
          </div>
          <p style={{ color: '#ccc', fontSize: '12px', lineHeight: '1.7', margin: 0 }}>{brief.jake_brief}</p>
        </div>
      )}

      {/* Per-Truck P&L */}
      <div style={{ marginBottom: '28px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <div style={{ color: '#32CD32', fontSize: '10px', letterSpacing: '2px', fontWeight: 700 }}>PER-TRUCK P&L</div>
          <button onClick={() => exportCSV(truckData, 'truck-pnl.csv')} style={{ ...smallBtn, background: 'transparent', border: '1px solid #32CD32', color: '#32CD32' }}>↓ CSV</button>
        </div>
        {drillTruck ? (
          <div style={{ border: '1px solid #1e1e1e', padding: '16px', background: '#050505' }}>
            <button onClick={() => setDrillTruck(null)} style={{ color: '#32CD32', background: 'none', border: 'none', cursor: 'pointer', marginBottom: '12px', fontFamily: 'monospace' }}>← BACK</button>
            <div style={{ color: '#fff', fontWeight: 700, marginBottom: '8px' }}>Unit {drillTruck.unitNumber} — {drillTruck.assetType}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '10px' }}>
              {[
                { label: 'REVENUE', val: `$${drillTruck.revenue.toLocaleString()}` },
                { label: 'FUEL COST', val: `$${drillTruck.fuel.toFixed(0)}` },
                { label: 'DRIVER PAY', val: `$${drillTruck.driverPay.toFixed(0)}` },
                { label: 'MAINT COST', val: `$${drillTruck.maint.toFixed(0)}` },
                { label: 'NET PROFIT', val: `$${drillTruck.net.toFixed(0)}`, bold: true },
                { label: 'RPM', val: `$${drillTruck.rpm.toFixed(2)}/mi` },
                { label: 'COST/MILE', val: `$${drillTruck.cpm.toFixed(3)}/mi` },
                { label: 'MARGIN', val: `${drillTruck.margin.toFixed(1)}%` },
              ].map((k, i) => (
                <div key={i} style={{ background: '#0a0a0a', border: '1px solid #1a1a1a', padding: '10px' }}>
                  <div style={{ color: '#555', fontSize: '9px', marginBottom: '4px' }}>{k.label}</div>
                  <div style={{ color: '#32CD32', fontSize: '16px', fontWeight: 700 }}>{k.val}</div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <table style={tableStyle}>
            <thead><tr>
              {[['unitNumber','UNIT #'],['loads','LOADS'],['revenue','REVENUE'],['fuel','FUEL'],['driverPay','DRIVER PAY'],['maint','MAINT'],['net','NET PROFIT'],['cpm','CPM'],['rpm','RPM'],['margin','MARGIN'],['health','HEALTH']].map(([col, label]) => (
                <Th key={col} col={col} label={label} sort={truckSort} setSort={setTruckSort} />
              ))}
            </tr></thead>
            <tbody>
              {sortTable(truckData, truckSort).map((t: any, i: number) => (
                <tr key={i} style={trStyle} onClick={() => setDrillTruck(t)}
                  onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = '#0d0d0d'}
                  onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = ''}>
                  <td style={{ padding: '10px 12px', color: '#32CD32', fontWeight: 700, cursor: 'pointer' }}>{t.unitNumber}</td>
                  <td style={{ padding: '10px 12px', color: '#aaa' }}>{t.loads}</td>
                  <td style={{ padding: '10px 12px', color: '#32CD32' }}>${t.revenue.toLocaleString()}</td>
                  <td style={{ padding: '10px 12px', color: '#ff4444' }}>${t.fuel.toFixed(0)}</td>
                  <td style={{ padding: '10px 12px', color: '#aaa' }}>${t.driverPay.toFixed(0)}</td>
                  <td style={{ padding: '10px 12px', color: '#aaa' }}>${t.maint.toFixed(0)}</td>
                  <td style={{ padding: '10px 12px', color: t.net >= 0 ? '#32CD32' : '#ff4444', fontWeight: 700 }}>${t.net.toFixed(0)}</td>
                  <td style={{ padding: '10px 12px', color: '#aaa' }}>${t.cpm.toFixed(3)}</td>
                  <td style={{ padding: '10px 12px', color: t.rpm >= 2 ? '#32CD32' : t.rpm >= 1.5 ? '#FFD700' : '#ff4444' }}>${t.rpm.toFixed(2)}</td>
                  <td style={{ padding: '10px 12px', color: t.margin >= 25 ? '#32CD32' : t.margin >= 10 ? '#FFD700' : '#ff4444' }}>{t.margin.toFixed(1)}%</td>
                  <td style={{ padding: '10px 12px' }}><span style={{ color: healthColor(t.health), fontSize: '10px', fontWeight: 700, border: `1px solid ${healthColor(t.health)}`, padding: '2px 6px' }}>{t.health}/100</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Per-Driver P&L */}
      <div style={{ marginBottom: '28px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <div style={{ color: '#32CD32', fontSize: '10px', letterSpacing: '2px', fontWeight: 700 }}>PER-DRIVER P&L</div>
          <button onClick={() => exportCSV(driverData, 'driver-pnl.csv')} style={{ ...smallBtn, background: 'transparent', border: '1px solid #32CD32', color: '#32CD32' }}>↓ CSV</button>
        </div>
        <table style={tableStyle}>
          <thead><tr>
            {[['name','DRIVER'],['loads','LOADS'],['revenue','REVENUE'],['pay','DRIVER PAY'],['netContribution','NET CONTRIBUTION'],['onTimeRate','ON-TIME %'],['avgRpm','AVG RPM']].map(([col, label]) => (
              <Th key={col} col={col} label={label} sort={driverSort} setSort={setDriverSort} />
            ))}
          </tr></thead>
          <tbody>
            {sortTable(driverData, driverSort).map((d: any, i: number) => (
              <tr key={i} style={trStyle}>
                <td style={{ padding: '10px 12px', fontWeight: 700 }}>{d.name}</td>
                <td style={{ padding: '10px 12px', color: '#aaa' }}>{d.loads}</td>
                <td style={{ padding: '10px 12px', color: '#32CD32' }}>${d.revenue.toLocaleString()}</td>
                <td style={{ padding: '10px 12px', color: '#aaa' }}>${d.pay.toFixed(0)}</td>
                <td style={{ padding: '10px 12px', color: d.netContribution >= 0 ? '#32CD32' : '#ff4444', fontWeight: 700 }}>${d.netContribution.toFixed(0)}</td>
                <td style={{ padding: '10px 12px', color: d.onTimeRate >= 90 ? '#32CD32' : d.onTimeRate >= 75 ? '#FFD700' : '#ff4444' }}>{d.onTimeRate.toFixed(0)}%</td>
                <td style={{ padding: '10px 12px', color: d.avgRpm >= 2 ? '#32CD32' : d.avgRpm >= 1.5 ? '#FFD700' : '#ff4444' }}>${d.avgRpm.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* IFTA + QuickBooks Export */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px', marginBottom:'24px' }}>
        <div style={{ border:'1px solid #1e1e1e', padding:'16px', background:'#050505' }}>
          <div style={{ color:'#32CD32', fontSize:'10px', letterSpacing:'2px', fontWeight:700, marginBottom:'8px' }}>IFTA FUEL TAX REPORT</div>
          <div style={{ color:'#555', fontSize:'11px', marginBottom:'12px' }}>Quarterly mileage by state + tax owed. Download CSV for your IFTA filing.</div>
          {(['Q1','Q2','Q3','Q4'] as const).map(q => {
            const year = new Date().getFullYear();
            const qNum = q.replace('Q','');
            const quarter = `${year}-${q}`;
            return (
              <button key={q} onClick={async () => {
                const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/ifta-report?companyId=${companyId}&quarter=${quarter}&format=csv`;
                const res = await fetch(url, { headers: { apikey: process.env.REACT_APP_SUPABASE_ANON_KEY || '' } });
                const blob = await res.blob();
                const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `IFTA_${quarter}.csv`; a.click();
              }} style={{ ...smallBtn, background:'transparent', border:'1px solid #555', color:'#aaa', marginRight:'6px', marginBottom:'6px' }}>
                ↓ {year} {q}
              </button>
            );
          })}
        </div>
        <div style={{ border:'1px solid #1e1e1e', padding:'16px', background:'#050505' }}>
          <div style={{ color:'#32CD32', fontSize:'10px', letterSpacing:'2px', fontWeight:700, marginBottom:'8px' }}>QUICKBOOKS EXPORT</div>
          <div style={{ color:'#555', fontSize:'11px', marginBottom:'12px' }}>Revenue, driver pay, fuel, and maintenance in QuickBooks IIF or CSV format.</div>
          <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' as const }}>
            {[['iif','QuickBooks IIF'],['csv','General Ledger CSV']].map(([fmt, label]) => (
              <button key={fmt} onClick={async () => {
                const end = new Date().toISOString().split('T')[0];
                const start = new Date(Date.now() - 90*86400000).toISOString().split('T')[0];
                const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/quickbooks-export?companyId=${companyId}&startDate=${start}&endDate=${end}&type=${fmt}`;
                const res = await fetch(url, { headers: { apikey: process.env.REACT_APP_SUPABASE_ANON_KEY || '' } });
                const blob = await res.blob();
                const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `Export_${start}_${end}.${fmt === 'iif' ? 'iif' : 'csv'}`; a.click();
              }} style={{ ...smallBtn, background:'transparent', border:'1px solid #555', color:'#aaa' }}>↓ {label}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Per-Lane P&L */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <div style={{ color: '#32CD32', fontSize: '10px', letterSpacing: '2px', fontWeight: 700 }}>PER-LANE P&L</div>
          <button onClick={() => exportCSV(laneData, 'lane-pnl.csv')} style={{ ...smallBtn, background: 'transparent', border: '1px solid #32CD32', color: '#32CD32' }}>↓ CSV</button>
        </div>
        <table style={tableStyle}>
          <thead><tr>
            {[['origin','ORIGIN'],['destination','DESTINATION'],['loads','LOADS'],['avgRate','AVG RATE'],['avgRpm','AVG RPM'],['avgMargin','AVG MARGIN'],['totalNet','TOTAL NET']].map(([col, label]) => (
              <Th key={col} col={col} label={label} sort={laneSort} setSort={setLaneSort} />
            ))}
          </tr></thead>
          <tbody>
            {sortTable(laneData, laneSort).map((l: any, i: number) => (
              <tr key={i} style={trStyle}>
                <td style={{ padding: '10px 12px', color: '#aaa' }}>{l.origin}</td>
                <td style={{ padding: '10px 12px', color: '#aaa' }}>{l.destination}</td>
                <td style={{ padding: '10px 12px', color: '#aaa' }}>{l.loads}</td>
                <td style={{ padding: '10px 12px', color: '#32CD32' }}>${l.avgRate.toFixed(0)}</td>
                <td style={{ padding: '10px 12px', color: l.avgRpm >= 2 ? '#32CD32' : l.avgRpm >= 1.5 ? '#FFD700' : '#ff4444' }}>${l.avgRpm.toFixed(2)}</td>
                <td style={{ padding: '10px 12px', color: l.avgMargin >= 25 ? '#32CD32' : l.avgMargin >= 10 ? '#FFD700' : '#ff4444' }}>{l.avgMargin.toFixed(1)}%</td>
                <td style={{ padding: '10px 12px', color: l.totalNet >= 0 ? '#32CD32' : '#ff4444', fontWeight: 700 }}>${l.totalNet.toFixed(0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
